import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Input } from '../../design/Input';
import { MotdText } from '../mc/MotdText';
import { Field, ToggleRow, Segmented, EffectBadge } from './primitives';

const DIFFICULTY = [
  { value: 'peaceful', label: 'Friedlich' },
  { value: 'easy', label: 'Leicht' },
  { value: 'normal', label: 'Normal' },
  { value: 'hard', label: 'Schwer' },
];
const GAMEMODES = [
  { value: 'survival', label: 'Überleben' },
  { value: 'creative', label: 'Kreativ' },
  { value: 'adventure', label: 'Abenteuer' },
  { value: 'spectator', label: 'Zuschauer' },
];

type Range = [min: number, max: number, label: string];
const RANGES: Record<'java' | 'bedrock', Record<string, Range>> = {
  java: {
    'max-players': [1, 500, 'Max. Spieler'],
    'view-distance': [2, 32, 'Sichtweite'],
    'simulation-distance': [2, 32, 'Simulationsweite'],
    'spawn-protection': [0, 512, 'Spawn-Schutz'],
  },
  bedrock: {
    'max-players': [1, 200, 'Max. Spieler'],
    'view-distance': [5, 96, 'Sichtweite'],
  },
};

/** Gleiche Grenzen wie das Backend (services/server-properties.js) – mit deutscher Meldung. */
export const validateProps = (kind: 'java' | 'bedrock', values: Record<string, any>): string | null => {
  for (const [key, [min, max, label]] of Object.entries(RANGES[kind])) {
    if (!(key in values)) continue;
    const v = values[key];
    if (!Number.isInteger(v) || v < min || v > max) return `${label}: bitte eine Zahl von ${min} bis ${max}.`;
  }
  const text = kind === 'bedrock' ? values['server-name'] : values.motd;
  const limit = kind === 'bedrock' ? 60 : 150;
  if (String(text ?? '').length > limit) return `${kind === 'bedrock' ? 'Servername' : 'MOTD'}: höchstens ${limit} Zeichen.`;
  return null;
};

interface Props {
  kind: 'java' | 'bedrock';
  values: Record<string, any>;
  online: boolean;
  onChange: (key: string, value: any) => void;
}

/** Die Spielwelt-Einstellungen aus server.properties – verständlich beschriftet statt roher Keys. */
export const GameRulesFields = ({ kind, values, online, onChange }: Props) => {
  const [more, setMore] = useState(false);
  const badge = (live: boolean) => (online ? <EffectBadge live={live} /> : null);
  const num = (key: string) => (
    <Input
      type="number"
      inputMode="numeric"
      min={RANGES[kind][key]?.[0]}
      max={RANGES[kind][key]?.[1]}
      value={values[key] ?? ''}
      onChange={(e) => onChange(key, e.target.value === '' ? '' : Number(e.target.value))}
    />
  );
  // &6, &l … wie in vielen Minecraft-Tools gewohnt – der Server braucht §.
  const withColors = (s: string) => s.replace(/&([0-9a-fk-or])/gi, '§$1');

  if (kind === 'bedrock') {
    return (
      <>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Servername" badge={badge(false)} hint="So heißt der Server in der Liste der Spieler.">
            <Input value={values['server-name'] ?? ''} maxLength={60} onChange={(e) => onChange('server-name', withColors(e.target.value))} />
          </Field>
          <Field label="Max. Spieler" badge={badge(false)}>{num('max-players')}</Field>
        </div>
        <Field label="Schwierigkeit" badge={badge(false)}>
          <Segmented value={values.difficulty} options={DIFFICULTY} onChange={(v) => onChange('difficulty', v)} />
        </Field>
        <Field label="Spielmodus" badge={badge(false)}>
          <Segmented value={values.gamemode} options={GAMEMODES.slice(0, 3)} onChange={(v) => onChange('gamemode', v)} />
        </Field>
        <div className="space-y-4 pt-1">
          <ToggleRow label="Allowlist" badge={badge(false)} hint="Nur freigeschaltete Spieler kommen rein." value={!!values['allow-list']} onChange={(v) => onChange('allow-list', v)} />
          <ToggleRow label="Cheats erlauben" badge={badge(false)} hint="Befehle wie /give und /tp für Operatoren." value={!!values['allow-cheats']} onChange={(v) => onChange('allow-cheats', v)} />
        </div>
        <Field label="Sichtweite (Chunks)" badge={badge(false)} hint="Höher = weiter sehen, aber mehr Last. Standard 32.">{num('view-distance')}</Field>
      </>
    );
  }

  return (
    <>
      <Field
        label="Nachricht in der Serverliste (MOTD)"
        badge={badge(false)}
        hint={<>So sehen Spieler deinen Server in ihrer Liste. Farben mit <code className="text-fg">&amp;6</code> (Gold), <code className="text-fg">&amp;a</code> (Grün), <code className="text-fg">&amp;l</code> (fett) …</>}
      >
        <Input value={values.motd ?? ''} maxLength={150} onChange={(e) => onChange('motd', withColors(e.target.value))} />
        <div className="rounded-lg bg-black/40 border border-line px-3 py-2 font-mono text-[13px] min-h-[36px]">
          <MotdText text={values.motd || ''} />
        </div>
      </Field>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Max. Spieler" badge={badge(false)}>{num('max-players')}</Field>
      </div>

      <Field
        label="Schwierigkeit"
        badge={badge(true)}
        hint={values.hardcore ? 'Im Hardcore-Modus ist die Schwierigkeit immer „Schwer".' : undefined}
      >
        {values.hardcore ? (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/25 border border-line text-xs text-fg">
            <span className="px-2 py-0.5 rounded-md bg-red-500/15 text-red-300 font-medium">Schwer</span>
            fest durch Hardcore
          </div>
        ) : (
          <Segmented value={values.difficulty} options={DIFFICULTY} onChange={(v) => onChange('difficulty', v)} />
        )}
      </Field>
      <Field label="Spielmodus für neue Spieler" badge={badge(false)} hint="Wer schon gespielt hat, behält seinen Modus – einzelne Spieler änderst du im Tab Spieler.">
        <Segmented value={values.gamemode} options={GAMEMODES} onChange={(v) => onChange('gamemode', v)} />
      </Field>
      {/* Hardcore ist in Minecraft kein Spielmodus, sondern ein eigener Schalter in server.properties. */}
      <ToggleRow
        label="Hardcore – nur ein Leben"
        badge={badge(false)}
        hint="Wer stirbt, kann nur noch zuschauen; die Schwierigkeit ist fest auf Schwer. Wirkt ab dem nächsten Start – am besten mit einer neuen Welt."
        value={!!values.hardcore}
        onChange={(v) => {
          onChange('hardcore', v);
          if (v) onChange('difficulty', 'hard');
        }}
      />

      <div className="space-y-4 pt-1">
        <ToggleRow
          label="Whitelist"
          badge={badge(true)}
          hint="Nur freigeschaltete Spieler kommen rein. Ohne Whitelist kann jeder mit der Adresse joinen – auch Server-Scanner."
          value={!!values['white-list']}
          onChange={(v) => onChange('white-list', v)}
        />
        <ToggleRow label="PvP" badge={badge(false)} hint="Spieler können sich gegenseitig Schaden machen." value={!!values.pvp} onChange={(v) => onChange('pvp', v)} />
        <ToggleRow
          label="Fliegen erlauben"
          badge={badge(false)}
          hint="Bei Modpacks mit Jetpacks, Flügeln & Co. anschalten – sonst wirft der Server Spieler fürs Fliegen raus."
          value={!!values['allow-flight']}
          onChange={(v) => onChange('allow-flight', v)}
        />
      </div>

      <div className="pt-1">
        <button type="button" onClick={() => setMore((m) => !m)} className="text-xs font-medium text-fg-muted hover:text-fg inline-flex items-center gap-1.5">
          <ChevronDown size={14} className={cn('transition-transform', more && 'rotate-180')} />
          Erweitert: Sichtweite &amp; Spawn-Schutz
        </button>
        {more && (
          <div className="grid sm:grid-cols-3 gap-5 mt-4">
            <Field label="Sichtweite" badge={badge(false)} hint="In Chunks (2–32). Weniger = weniger Last.">{num('view-distance')}</Field>
            <Field label="Simulationsweite" badge={badge(false)} hint="Wie weit Farmen & Mobs laufen (2–32).">{num('simulation-distance')}</Field>
            <Field label="Spawn-Schutz" badge={badge(false)} hint="Blöcke um den Spawn, die nur Operatoren ändern dürfen.">{num('spawn-protection')}</Field>
          </div>
        )}
      </div>
    </>
  );
};
